import { CLINIC_TIMEZONE, SCHEDULE_SLOT_MINUTES } from './constants.js';
function asDate(value) {
    const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    if (Number.isNaN(parsed.getTime()))
        throw new Error('Invalid date value.');
    return parsed;
}
export function validateTimeRange(range, maximumMinutes = 12 * 60) {
    const start = asDate(range.startAt);
    const end = asDate(range.endAt);
    const durationMinutes = (end.getTime() - start.getTime()) / 60_000;
    if (durationMinutes <= 0 || !Number.isInteger(durationMinutes)) {
        throw new Error('The end time must be after the start time in whole minutes.');
    }
    if (durationMinutes > maximumMinutes)
        throw new Error('The time range exceeds the allowed duration.');
    return { start, end, durationMinutes };
}
export function rangesOverlap(left, right) {
    const a = validateTimeRange(left);
    const b = validateTimeRange(right);
    return a.start < b.end && b.start < a.end;
}
function zonedKey(date, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? '';
    return `${part('year')}${part('month')}${part('day')}_${part('hour')}${part('minute')}`;
}
export function scheduleClaimIds(providerId, range, timezone = CLINIC_TIMEZONE) {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(providerId))
        throw new Error('Invalid provider identifier.');
    const { start, end, durationMinutes } = validateTimeRange(range);
    if (durationMinutes % SCHEDULE_SLOT_MINUTES !== 0) {
        throw new Error(`Duration must use ${SCHEDULE_SLOT_MINUTES}-minute increments.`);
    }
    if (start.getUTCMinutes() % SCHEDULE_SLOT_MINUTES !== 0) {
        throw new Error(`Start time must use ${SCHEDULE_SLOT_MINUTES}-minute increments.`);
    }
    const claims = [];
    for (let instant = start.getTime(); instant < end.getTime(); instant += SCHEDULE_SLOT_MINUTES * 60_000) {
        claims.push(`${providerId}_${zonedKey(new Date(instant), timezone)}`);
    }
    return claims;
}
const lifecycleTransitions = {
    pending_scheduling_confirmation: ['scheduled', 'no_show', 'rescheduled', 'cancelled'],
    scheduled: ['confirmed', 'completed', 'no_show', 'rescheduled', 'cancelled'],
    confirmed: ['completed', 'no_show', 'rescheduled', 'cancelled'],
    completed: ['no_show', 'rescheduled', 'cancelled'],
    no_show: ['rescheduled', 'cancelled'],
    rescheduled: [],
    cancelled: [],
};
export function canTransitionLifecycle(from, to) {
    return lifecycleTransitions[from]?.includes(to) ?? false;
}
export function effectiveAppointmentLifecycle(lifecycleStatus, endAt, referenceAt = new Date()) {
    if (['pending_scheduling_confirmation', 'scheduled', 'confirmed'].includes(lifecycleStatus) &&
        asDate(endAt) <= asDate(referenceAt)) {
        return 'completed';
    }
    return lifecycleStatus;
}
//# sourceMappingURL=scheduling.js.map